import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { login, userByToken } from "../api/auth";
import { addUser, setUser } from "../api/userService";

// 🔒 Claves fijas para localStorage
import { AUTH_TOKEN_KEY, AUTH_USER_ID_KEY, AUTH_REFRESH_TOKEN_KEY } from "../constants";

export const fetchUserBytoken = createAsyncThunk(
  "auth/fetchUserByToken",
  async ({ id, token }, thunkAPI) => {
    if (!id || !token || token === "undefined" || id === "undefined") {
      return thunkAPI.rejectWithValue({ message: "Token o ID inválido" });
    }

    try {
      const response = await userByToken(id, token);
      if (response.status === 200) {
        return { ...response.data };
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e?.response?.data || { message: "Error al obtener usuario" }
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, thunkAPI) => {
    try {
      const response = await login(email, password);
      if (response.status === 200) {
        return response.data;
      } else {
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e?.response?.data || { message: "Error al iniciar sesión" }
      );
    }
  }
);

export const createUser = createAsyncThunk(
  "auth/createUser",
  async (userData, thunkAPI) => {
    try {
      const response = await addUser(userData);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e?.response?.data || { message: "Error al crear usuario" }
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async (userData, thunkAPI) => {
    try {
      const response = await setUser(userData);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(
        e?.response?.data || { message: "Error al actualizar usuario" }
      );
    }
  }
);

// Estado inicial
const initialState = {
  id: "",
  username: "",
  email: "",
  name: "",
  isFetching: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  folder_seller: "",
  folder_buyer: "",
  folder_background: "",
  folder_permission: "",
  folder_contract_original: "",
  folder_contract_signed: "",
  folder_f23_payment: "",
  folder_movements: "",
  folder_sii_cert: "",
  folder_rc_inscription: "",
  isInitialized: false, // Nuevo flag
};

// Slice
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearState: (state) => {
      Object.assign(state, initialState);
    },
    clearUserState: (state) => {
      // Limpiar solo el estado relacionado con creación/edición de usuarios
      // NO afectar la autenticación
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.isFetching = false;
      // Mantener la autenticación intacta
      // state.id = ""; // NO limpiar
      // state.name = ""; // NO limpiar
      // state.email = ""; // NO limpiar
      // state.role = ""; // NO limpiar
      // state.isInitialized = false; // NO limpiar
    },
    logout: (state) => {
      // Limpiar localStorage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_ID_KEY);
      // Resetear estado
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
        state.isInitialized = false;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        // Aplanar: el login devuelve { user: {...}, tokens: {...} }
        const user = payload.user || payload;
        const id = user?.id || user?._id || state.id;

        // Guardar tokens en localStorage
        const accessToken = payload.tokens?.accessToken || payload.tokens?.access?.token || payload.tokens?.access || payload.accessToken || payload.access;
        const refreshToken = payload.tokens?.refreshToken || payload.tokens?.refresh?.token || payload.tokens?.refresh || payload.refreshToken || payload.refresh;

        if (accessToken && typeof accessToken === 'string') {
          localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
        }
        if (refreshToken && typeof refreshToken === 'string') {
          localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
        }
        if (id) {
          localStorage.setItem(AUTH_USER_ID_KEY, id);
        }

        Object.assign(state, {
          ...user,  // aplanamos los campos del usuario directamente en el estado
          id,
          isFetching: false,
          isSuccess: true,
          isInitialized: true,
        });

      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = payload?.message || "Error al iniciar sesión";
        state.isInitialized = true;
      })

      // Fetch por token
      .addCase(fetchUserBytoken.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
        state.isInitialized = false;
      })
      .addCase(fetchUserBytoken.fulfilled, (state, { payload }) => {
        // Normalizar el id
        const id = payload.id || payload._id || state.id;
        
        // Guardar tokens si están presentes en la respuesta - verificar diferentes formatos
        const accessToken = payload.tokens?.accessToken || payload.tokens?.access?.token || payload.tokens?.access || payload.accessToken || payload.access;
        const refreshToken = payload.tokens?.refreshToken || payload.tokens?.refresh?.token || payload.tokens?.refresh || payload.refreshToken || payload.refresh;
        
        if (accessToken && typeof accessToken === 'string') {
          localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
        }
        if (refreshToken && typeof refreshToken === 'string') {
          localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
        }
        if (id) {
          localStorage.setItem(AUTH_USER_ID_KEY, id);
        }
        
        Object.assign(state, {
          ...payload,
          id, // aseguramos que id siempre esté presente
          isFetching: false,
          isSuccess: true,
          isInitialized: true,
        });

      })
      .addCase(fetchUserBytoken.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = payload?.message || "Error al obtener usuario";
        state.isInitialized = true;
        // Limpiar localStorage y estado si el token es inválido
        try {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_USER_ID_KEY);
          console.warn("[authSlice] Token inválido, limpiando sesión y estado");
        } catch (e) {
          console.error("[authSlice] Error limpiando localStorage", e);
        }
      })

      // Create User
      .addCase(createUser.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(createUser.fulfilled, (state) => {
        // Solo flags, nunca tocar autenticación
        state.isFetching = false;
        state.isSuccess = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(createUser.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = payload?.message || "Error al crear usuario";
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.isFetching = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(updateUser.fulfilled, (state) => {
        // Solo flags, nunca tocar autenticación
        state.isFetching = false;
        state.isSuccess = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(updateUser.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.isError = true;
        state.errorMessage = payload?.message || "Error al actualizar usuario";
      });
  },
});

// Exports
export const { clearState, clearUserState, logout } = authSlice.actions;
export const userSelector = (state) => state.auth;
export default authSlice.reducer;